import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  ArrowDownUp, Plus, Minus, Search, Package, Loader2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, History
} from 'lucide-react';

const ADJUSTMENT_TYPES = [
  { value: 'add', label: 'Add Stock', icon: TrendingUp, color: 'text-emerald-600' },
  { value: 'remove', label: 'Remove Stock', icon: TrendingDown, color: 'text-rose-600' },
  { value: 'damage', label: 'Damaged Goods', icon: AlertTriangle, color: 'text-amber-600' },
  { value: 'expired', label: 'Expired', icon: AlertTriangle, color: 'text-rose-600' },
  { value: 'correction', label: 'Inventory Correction', icon: ArrowDownUp, color: 'text-blue-600' },
];

export default function StockAdjustmentsPage() {
  const [products, setProducts] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const res = await api.get('/products', { params: { search, limit: 50 } });
      setProducts(res.data.products || []);
    } catch (err) {
      console.error('Products fetch error:', err);
    }
  }, [search]);

  const fetchAdjustments = useCallback(async () => {
    try {
      const res = await api.get('/stock-adjustments', { params: { limit: 50 } });
      setAdjustments(res.data.adjustments || []);
    } catch (err) {
      console.error('Adjustments fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchAdjustments();
  }, [fetchProducts, fetchAdjustments]);

  const handleAdjustment = async () => {
    if (!selectedProduct || !quantity || !reason.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/stock-adjustments', {
        product_id: selectedProduct.product_id,
        adjustment_type: adjustmentType,
        quantity: parseInt(quantity),
        reason: reason.trim()
      });
      toast.success('Stock adjustment recorded');
      setDialogOpen(false);
      setSelectedProduct(null);
      setQuantity(1);
      setReason('');
      fetchProducts();
      fetchAdjustments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record adjustment');
    } finally {
      setSubmitting(false);
    }
  };

  const openAdjustmentDialog = (product) => {
    setSelectedProduct(product);
    setAdjustmentType('add');
    setQuantity(1);
    setReason('');
    setDialogOpen(true);
  };

  const typeConfig = ADJUSTMENT_TYPES.find(t => t.value === adjustmentType);

  return (
    <div className="space-y-6" data-testid="stock-adjustments-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Stock Adjustments
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manual stock corrections with audit trail
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Products List */}
        <Card className="lg:col-span-2" data-testid="products-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <Package className="h-4 w-4 text-primary" /> Select Product
            </CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="product-search"
              />
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {products.map((product) => (
                  <div
                    key={product.product_id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => openAdjustmentDialog(product)}
                    data-testid={`product-${product.product_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{product.name}</p>
                      <p className="text-xs text-muted-foreground">{product.generic_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold">{product.stock_quantity} units</p>
                      <Badge
                        variant={product.stock_quantity <= 0 ? 'destructive' : product.stock_quantity <= product.min_stock_level ? 'secondary' : 'outline'}
                        className="text-[10px]"
                      >
                        {product.stock_quantity <= 0 ? 'Out' : product.stock_quantity <= product.min_stock_level ? 'Low' : 'OK'}
                      </Badge>
                    </div>
                    <Button variant="ghost" size="icon" className="ml-2 h-8 w-8">
                      <ArrowDownUp className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Adjustments */}
        <Card data-testid="adjustments-history">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <History className="h-4 w-4 text-primary" /> Recent Adjustments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : adjustments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No adjustments yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adjustments.map((adj) => {
                    const type = ADJUSTMENT_TYPES.find(t => t.value === adj.adjustment_type);
                    const Icon = type?.icon || ArrowDownUp;
                    return (
                      <div key={adj.adjustment_id} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-start gap-2">
                          <Icon className={`h-4 w-4 mt-0.5 ${type?.color || ''}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{adj.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              {adj.adjustment_type === 'add' ? '+' : '-'}{adj.quantity} units
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 italic">"{adj.reason}"</p>
                            <p className="text-[10px] text-muted-foreground mt-1">
                              {new Date(adj.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Adjustment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="adjustment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowDownUp className="h-4 w-4" /> Record Stock Adjustment
            </DialogTitle>
          </DialogHeader>
          {selectedProduct && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-muted/50">
                <p className="text-sm font-semibold">{selectedProduct.name}</p>
                <p className="text-xs text-muted-foreground">{selectedProduct.generic_name}</p>
                <p className="text-sm mt-2">Current Stock: <strong>{selectedProduct.stock_quantity}</strong> units</p>
              </div>

              <div className="space-y-2">
                <Label>Adjustment Type</Label>
                <Select value={adjustmentType} onValueChange={setAdjustmentType}>
                  <SelectTrigger data-testid="adjustment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ADJUSTMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className={`h-4 w-4 ${type.color}`} />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  min={1}
                  max={adjustmentType === 'add' ? 9999 : selectedProduct.stock_quantity}
                  data-testid="quantity-input"
                />
                {typeConfig && (
                  <p className="text-xs text-muted-foreground">
                    New stock will be: <strong>
                      {adjustmentType === 'add' 
                        ? selectedProduct.stock_quantity + parseInt(quantity || 0)
                        : Math.max(0, selectedProduct.stock_quantity - parseInt(quantity || 0))}
                    </strong> units
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Reason / Notes</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason for adjustment..."
                  className="h-20"
                  data-testid="reason-input"
                />
              </div>

              <Separator />

              <Button 
                className="w-full h-11 font-semibold" 
                onClick={handleAdjustment}
                disabled={submitting || !reason.trim()}
                data-testid="submit-adjustment"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Record Adjustment</>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
